import { useEffect, useState } from "react"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import styled, { ThemeProvider } from "styled-components"
import Menu from "./modules/Menu"
import ProfileModal from "./modules/ProfileModal"
import GlobalStyle from "./GlobalStyle"
import { lightTheme, darkTheme, BREAKPOINTS, Z, TAP_MIN } from "./GlobalTheme"
import Dropdown from "./components/Dropdown"
import { useTranslation } from "react-i18next"
import fbConf from "./FirebaseConf"
import { getAuth } from "firebase/auth"
import Button from "./components/Button"
import Icon from "./Icon"
import SignOutTimer from "./modules/SignOutTimer"
import StartupLoader from "./components/StartupLoader"
import { isAdminTokenUsable } from "./utils/adminToken"
import Comm from "./Comm"
import { ConfirmProvider } from "./ConfirmProvider"
import { NotificationProvider } from "./modules/Notification"
import { setTitleBase, setPageTitle, getStaticTitleKey } from "./utils/pageTitle"
import { LANGUAGES, languageByLabel, languageByCode, flagIcon } from "./languages"

const Sidebar = styled.div<{ $isMenuOpen: boolean }>`
  /* No box on desktop: children keep their existing flow and containing blocks exactly. */
  display: contents;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    display: block;
    position: fixed;
    top: 0px;
    left: 0px;
    bottom: 0px;
    width: 280px;
    max-width: 85vw;
    padding-top: 8px;
    box-sizing: border-box;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding-top: calc(8px + env(safe-area-inset-top));
    padding-bottom: env(safe-area-inset-bottom);
    background-color: ${props => props.theme.colors.background};
    box-shadow: ${props => props.$isMenuOpen ? '0px 0px 24px rgba(0,0,0,.18)' : 'none'};
    z-index: ${Z.drawer};
    transform: translateX(${props => props.$isMenuOpen ? '0' : '-101%'});
    transition: transform .22s ease-in-out;
  }
`

const Scrim = styled.div<{ $isMenuOpen: boolean }>`
  display: none;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    display: block;
    position: fixed;
    inset: 0px;
    background-color: rgba(0, 0, 0, .45);
    z-index: ${Z.drawerScrim};
    opacity: ${props => props.$isMenuOpen ? '1' : '0'};
    pointer-events: ${props => props.$isMenuOpen ? 'auto' : 'none'};
    transition: opacity .22s ease-in-out;
  }
`

const OutletContainer = styled.div<{ $isMenuOpen: boolean }>`
  position: absolute;
  right: 20px;
  bottom: 0px;
  height: 90%;
  width: ${props => props.$isMenuOpen ? '83%' : 'calc(100% - 20px - 2% - 30px - (16px * 2))'};
  transition: width .2s ease-in-out;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    /* Only below the breakpoint does this become the page scroller. Above it, desktop pages own
       their own scroll containers, and clipping here cuts off the charger tab strip, which is
       positioned at top: -1.5vh and so sits above this box. */
    overflow-y: auto;
    overscroll-behavior-y: contain;
    left: 0px;
    right: 0px;
    top: calc(68px + env(safe-area-inset-top));
    width: auto;
    height: calc(100dvh - 68px - env(safe-area-inset-top));
    padding: 0px max(12px, env(safe-area-inset-right)) env(safe-area-inset-bottom) max(12px, env(safe-area-inset-left));
    box-sizing: border-box;
  }
`

const LogoContainer = styled.div<{ $isMenuOpen: boolean }>`
  width: ${props => props.$isMenuOpen ? '10vw' : '2vw'};
  margin-left: ${props => props.$isMenuOpen ? '2.5vw' : '1.75vw'};;
  margin-top: 3.5vh;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: 160px;
    margin-left: 56px;
    margin-top: 12px;
  }
`

const DividerLine = styled.div<{ $isMenuOpen: boolean }>`
  width: ${props => props.$isMenuOpen ? '12vw' : 'calc(2vw + 30px + (16px * 2))'};
  height: 1px;
  margin-left: ${props => props.$isMenuOpen ? '1.6vw' : '0px'};
  margin-top: 1.6vh;
  margin-bottom: 2vh;
  background-image: linear-gradient(to right, transparent, #E0E1E2, transparent);
  transition: width .2s ease-in-out, margin-left .2s ease-in-out;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: auto;
    margin-left: 16px;
    margin-right: 16px;
    margin-top: 8px;
    margin-bottom: 8px;
    transition: none;
  }
`

const MenuTriggerButtonContainer = styled.div<{ $isMenuOpen: boolean }>`
  position: absolute;
  top: 5.25vh;
  margin-left: ${props => props.$isMenuOpen ? '13vw' : '4.25vw'};
  width: 16px;
  height: 16px;
  cursor: pointer;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    position: fixed;
    top: max(6px, env(safe-area-inset-top));
    left: max(6px, env(safe-area-inset-left));
    margin-left: 0px;
    width: ${TAP_MIN}px;
    height: ${TAP_MIN}px;
    padding: 12px;
    box-sizing: border-box;
    z-index: ${Z.drawer + 1};
  }
`

const TopRightMenuContainer = styled.div`
  position: absolute;
  top: 20px;
  right: 35px;
  display: flex;

  /* The menu trigger is a fixed 44px box at top/left 6px, so its centre is 28px down. Matching that
     here puts every top-bar control on one baseline; without it the theme and flag dropdowns (44px
     tap boxes) and the Signout text each landed at a different height. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    top: max(6px, env(safe-area-inset-top));
    right: max(8px, env(safe-area-inset-right));
    height: ${TAP_MIN}px;
    align-items: center;
  }
`

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 10px;
  padding: 0px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  color: ${props => props.theme.colors.gray_900};

  @media (max-width: ${BREAKPOINTS.shell}px) {
    min-height: ${TAP_MIN}px;
  }
`

const ProfileAvatar = styled.span`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: ${props => props.theme.colors.white};
  background-color: ${props => props.theme.colors.primary};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

// The name is what gets squeezed first; on a phone the avatar alone is the button.
const ProfileName = styled.span`
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: ${BREAKPOINTS.phone}px) {
    display: none;
  }
`

const BottomSectionContainerBig = styled.div`
  position: absolute;
  bottom: 0px;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    position: static;
    margin-top: 24px;
    padding-bottom: 16px;
  }
`

const FeedbackBackground = styled.div`
  position: relative;
  width: 11.5vw;
  height: 210px;
  margin-left: 2vw;
  background: linear-gradient(270deg, #64FFEB 0%, #2A6FF4 100%);
  border-radius: 16px;
  overflow: hidden;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: auto;
    height: auto;
    margin: 0px 12px;
    padding-bottom: 16px;
    overflow: visible;
  }
`

const FeedBackContainerBlob = styled.div`
  position: absolute;
  width: 10vw;
  height: 10vw;
  left: 2vw;
  bottom: 0px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 40%, #FF29C3 100%);
  border-radius: 100vw;
  filter: blur(30px);

  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: 160px;
    height: 160px;
    left: 12px;
  }
`

const FeedbackIconContainer = styled.div`
  width: 24px;
  height: 24px;
  background-color: ${props => props.theme.colors.white};
  padding: 6px;
  border-radius: 12px;
  margin-left: 16px;
  margin-top: 16px;
  position: relative;
`

const FeedbackTitle = styled.p`
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.colors.white};
  margin-top: 20px;
  margin-bottom: 0px;
  margin-left: 16px;
  position: relative;
`

const FeedbackLabel = styled.p`
  font-weight: 500;
  font-size: 12px;
  color: ${props => props.theme.colors.white};
  margin-top: 8px;
  margin-bottom: 26px;
  margin-left: 16px;
  position: relative;
`

const TimelockContainer = styled.div<{ $isMenuOpen: boolean }>`
  display: flex;
  margin-left: ${props => props.$isMenuOpen ? '0px' : '-24px'};
  margin-top: 16px;
  width: 110%;
  justify-content: center;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    margin-left: 0px;
    width: auto;
  }
`

const LastLoginLabel = styled.p<{ $isMenuOpen: boolean }>`
  width: ${props => props.$isMenuOpen ? '11.5vw' : '65px'};
  margin-left: ${props => props.$isMenuOpen ? '2vw' : '20px'};
  text-align: center;
  font-weight: 400;
  font-size: 10px;
  color: ${props => props.theme.colors.gray_600};
  margin-top: 8px;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: auto;
    margin-left: 12px;
    margin-right: 12px;
  }
`

const VersionLabel = styled.p<{ $isMenuOpen: boolean }>`
  width: ${props => props.$isMenuOpen ? '11.5vw' : '65px'};
  margin-left: ${props => props.$isMenuOpen ? '2vw' : '20px'};
  text-align: center;
  font-weight: 400;
  font-size: 10px;
  color: ${props => props.theme.colors.gray_600};
  margin-top: 16px;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: auto;
    margin-left: 12px;
    margin-right: 12px;
  }
`

const WhatsNewLink = styled.a<{ $isMenuOpen: boolean }>`
  display: ${props => props.$isMenuOpen ? 'inline' : 'block'};
  font-weight: 600;
  color: inherit;

  &:hover {
    color: ${props => props.theme.colors.gray_900};
  }
`

const ServerUnavailableLabel = styled.p`
  position: absolute;
  top: -16px;
  width: 20%;
  left: 40%;
  text-align: center;
  font-weight: 700;
  font-size: 16px;
  padding-top: 8px;
  padding-bottom: 8px;
  color: ${props => props.theme.colors.white};
  background-color: ${props => props.theme.colors.danger};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`

function App() {
  const [VERSION] = useState<string>('v26.10.28')

  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const location = useLocation()

  const [isAdmin, setIsAdmin] = useState<boolean>()
  const [isProfileOpen, setIsProfileOpen] = useState<boolean>(false)
  const [isProfilePhotoBroken, setIsProfilePhotoBroken] = useState<boolean>(false)

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(localStorage.getItem('preferMenuClosed') === 'false' || !localStorage.getItem('preferMenuClosed'))
  const [selectedMenuItem, setSelectedMenuItem] = useState<string>('dashboard')
  const [theme, setTheme] = useState<string>(localStorage.getItem('preferedTheme') == 'system' ? (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : localStorage.getItem('preferedTheme') as string)
  const [signoutTimerResetTrigger, setSignoutTimerResetTrigger] = useState(0)
  const [isServerUnavailable, setIsServerUnavailable] = useState<boolean>(false)

  // The drawer is the one place mobile needs real behaviour, not just CSS. All three effects are
  // width-guarded so the desktop sidebar (and its stored preference) are never touched.
  const isShellMobile = () => window.innerWidth <= BREAKPOINTS.shell

  useEffect(() => {
    // Stored preference defaults to OPEN, which on a phone would cover the page on first paint.
    if (isShellMobile()) setIsMenuOpen(false)
  }, [])

  useEffect(() => {
    if (isShellMobile()) setIsMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && isShellMobile()) setIsMenuOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Redirect non-Admin user to dashboard if on a non-approved page
  useEffect(() => {
    if (!isAdmin && isAdmin != undefined) {
      const userApprovedPaths = ['', 'chargers', 'rfids']
      if (!userApprovedPaths.includes(location.pathname.split('/')[1])) {
        navigate('/chargers')
      }
    }
  }, [location.pathname])

  // Keep the browser tab title in sync with the current static page (VLT-1973).
  // Data-dependent pages (charger detail, charger logs) set their own title from within the page.
  useEffect(() => {
    const titleKey = getStaticTitleKey(location.pathname)
    if (titleKey) setPageTitle(t(titleKey))
  }, [location.pathname, isAdmin, i18n.language])


  // Redirect to login page if not logged in, decide if admin if logged in
  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/login')
    } else {
      decideIfUserIsAdmin()
    }
  }, [])

  // Re-persist the Firebase ID token on every refresh so it doesn't go stale after ~1h (VLT-2043).
  // Skip admins: their token is a separate server-minted JWT, not the Firebase ID token.
  useEffect(() => {
    const unsubscribe = getAuth(fbConf).onIdTokenChanged(async (user) => {
      if (!user) return
      const idTokenResult = await user.getIdTokenResult()
      if (idTokenResult.claims.admin) return
      localStorage.setItem('token', idTokenResult.token)
    })
    return () => unsubscribe()
  }, [])

  async function decideIfUserIsAdmin() {
    try {
      // Resolves once Firebase has restored the saved session or found none, so a dead session goes
      // to the login page at once instead of after 10 seconds of polling (VLT-3185).
      await getAuth(fbConf).authStateReady()
      const idTokenResult = await getAuth(fbConf).currentUser?.getIdTokenResult()
      // Everyone else's token is Firebase's own, which Comm refreshes before every request.
      if (!idTokenResult || (idTokenResult.claims.admin && !isAdminTokenUsable(localStorage.getItem('token')))) {
        handleSignOut()
        return
      }
      setIsAdmin(idTokenResult.claims.admin ? true : false)
      // Set the title base; the page-name part is applied by the effect below (and by data-dependent pages)
      setTitleBase(idTokenResult.claims.admin ? true : false)
      console.log(`Admin: ${idTokenResult.claims.admin ? true : false}`)
      // Only once the session is known to be good: with a dead token its 401 would start a second, delayed redirect.
      pingServerToSeeIfOnline()
    } catch {
      handleSignOut()
    }
  }

  function handleSwitchMenuState() {
    setIsMenuOpen(!isMenuOpen)
    localStorage.setItem('preferMenuClosed', isMenuOpen.toString())
  }

  // Set language to preferred
  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem('preferedLanguage') || 'en')
  }, [])

  function handleChooseDropdownOption(label: string) {
    // The Dropdown reports the chosen label, so the language list resolves it back to a code.
    const language = languageByLabel(label)
    if (language) {
      i18n.changeLanguage(language.code)
      localStorage.setItem('preferedLanguage', language.code)
      return
    }

    switch (label) {
      case t('login_light'):
        setTheme('light')
        localStorage.setItem('preferedTheme', 'light')
        break
      case t('login_dark'):
        setTheme('dark')
        localStorage.setItem('preferedTheme', 'dark')
        break
      case t('login_system'):
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
          setTheme('dark')
        } else {
          setTheme('light')
        }
        localStorage.setItem('preferedTheme', 'system')
        break
    }
  }

  async function handleSignOut() {
    // Only call the signOut method if a user is signed in, unless it might hang up on the await
    if (getAuth(fbConf).currentUser) {
      await getAuth(fbConf).signOut()
    }
    localStorage.removeItem('token')
    navigate(`/login?redirect=${location.pathname}`)
  }

  // Detect light/dark theme change
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (event: MediaQueryListEvent) => {
      if (localStorage.getItem('preferedTheme') == 'system') {
        event.matches ? setTheme('dark') : setTheme('light')
      }
    }
    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [])

  // Ping websocket microservice to check if server is available
  async function pingServerToSeeIfOnline() {
    const ping = await Comm('/api-websocket/online', {})
    //const ping = await Comm('https://httpstat.us/429', {})
    if (ping.status == 429) {
      setIsServerUnavailable(true)
    }
  }

  // Only a loader until we know who is signed in, so a signed-out visitor never sees the dashboard flash
  // before the redirect to the login page (VLT-3185).
  if (isAdmin == undefined) {
    return (
      <ThemeProvider theme={theme == 'dark' ? darkTheme : lightTheme}>
        <GlobalStyle />
        <StartupLoader />
      </ThemeProvider>
    )
  }

  return (
    <div onClick={() => setSignoutTimerResetTrigger((prev: number) => prev + 1)}>
      <ConfirmProvider>
        <ThemeProvider theme={theme == 'dark' ? darkTheme : lightTheme}>
          <NotificationProvider>
            <GlobalStyle />
            {isServerUnavailable && isAdmin && (
              <ServerUnavailableLabel>{t('app_serverUnavailable')}</ServerUnavailableLabel>
            )}
            <TopRightMenuContainer>
              <Dropdown handleChoose={handleChooseDropdownOption} title={{ icon: localStorage.getItem('preferedTheme') == 'system' ? "app.systemTheme" : theme == 'dark' ? "app.darkmode" : "app.lightmode", label: '', color: "gray_600" }} options={[{ icon: "app.lightmode", label: t('login_light'), color: "gray_600" }, { icon: "app.darkmode", label: t('login_dark'), color: "gray_600" }, { icon: "app.systemTheme", label: t('login_system'), color: "gray_600" }]} />
              <Dropdown handleChoose={handleChooseDropdownOption} title={{ icon: flagIcon(languageByCode(i18n.language)?.code || 'en'), label: '' }} options={LANGUAGES.map(language => ({ icon: flagIcon(language.code), label: language.label }))} />
              {(() => {
                // isAdmin is only decided once the Firebase user has loaded, so currentUser is set here.
                const currentUser = getAuth(fbConf).currentUser
                const name = currentUser?.displayName || currentUser?.email || ''
                return (
                  <ProfileButton aria-label={t('app_profile')} onClick={() => setIsProfileOpen(true)}>
                    <ProfileAvatar>
                      {currentUser?.photoURL && !isProfilePhotoBroken
                        ? <img src={currentUser.photoURL} alt="" referrerPolicy="no-referrer" onError={() => setIsProfilePhotoBroken(true)} />
                        : name.charAt(0).toUpperCase()}
                    </ProfileAvatar>
                    <ProfileName>{name}</ProfileName>
                  </ProfileButton>
                )
              })()}
            </TopRightMenuContainer>
            {isProfileOpen && (
              <ProfileModal isAdmin={isAdmin} onClose={() => setIsProfileOpen(false)} onSignOut={handleSignOut} />
            )}
            <Scrim $isMenuOpen={isMenuOpen} onClick={() => setIsMenuOpen(false)} />
            <Sidebar $isMenuOpen={isMenuOpen}>
            <LogoContainer $isMenuOpen={isMenuOpen}>
              <Icon path={isMenuOpen ? "general.logoDashboardHorizontal" : "general.charger"} width="100%" height="100%" color="none" />
            </LogoContainer>
            <DividerLine $isMenuOpen={isMenuOpen} />
            <Menu isMenuOpen={isMenuOpen} selectedMenuItem={selectedMenuItem} setSelectedMenuItem={setSelectedMenuItem} isAdmin={isAdmin} />
            <BottomSectionContainerBig>
              {isMenuOpen ? (
                <FeedbackBackground>
                  <FeedBackContainerBlob />
                  <FeedbackIconContainer>
                    <Icon path="app.mail" width="100%" height="100%" />
                  </FeedbackIconContainer>
                  <FeedbackTitle>{t('app_missingSomething')}</FeedbackTitle>
                  <FeedbackLabel>{t('app_helpUsImporveInTheRightDirection')}</FeedbackLabel>
                  <div style={{ position: 'relative', marginLeft: '16px' }}>
                    <Button label={t('app_sendFeedback')} width='max(calc(11.5vw - 32px), 115px)' onclick={() => window.open(`mailto:help@voltie.eu?subject=Voltie Dashboard visszajelzés`)} bgcolor="white" labelcolor="black"></Button>
                  </div>
                </FeedbackBackground>
              ) : (
                <div style={{ marginLeft: '16px', cursor: 'pointer' }} onClick={() => window.open(`mailto:help@voltie.eu?subject=Voltie Dashboard visszajelzés`)}>
                  <FeedbackIconContainer>
                    <Icon path="app.mail" width="100%" height="100%" />
                  </FeedbackIconContainer>
                </div>
              )}
              <TimelockContainer $isMenuOpen={isMenuOpen}>
                <Icon path="app.key" width="16px" height="16px" color="gray_600" />
                <SignOutTimer isMenuOpen={isMenuOpen} triggerReset={signoutTimerResetTrigger} />
              </TimelockContainer>
              <LastLoginLabel $isMenuOpen={isMenuOpen}>{`${t('app_lastLogin')}: ${localStorage.getItem('lastLogin') || '-'}`}</LastLoginLabel>
              <VersionLabel $isMenuOpen={isMenuOpen}>
                {VERSION}{isMenuOpen && ' · '}
                <WhatsNewLink $isMenuOpen={isMenuOpen} href={`https://whatsnew.voltie.eu/?lang=${i18n.language}&theme=${theme == 'dark' ? 'dark' : 'light'}`} target="_blank" rel="noopener">{t('app_whatsNew')}</WhatsNewLink>
              </VersionLabel>
            </BottomSectionContainerBig>
            </Sidebar>
            <OutletContainer $isMenuOpen={isMenuOpen}>
              <Outlet context={{ theme, isAdmin }} />
            </OutletContainer>
            <MenuTriggerButtonContainer $isMenuOpen={isMenuOpen} onClick={handleSwitchMenuState}>
              <Icon path={isMenuOpen ? "general.backArrow" : "general.forwardArrow"} width="100%" height="100%" color="primary" />
            </MenuTriggerButtonContainer>
          </NotificationProvider>
        </ThemeProvider>
      </ConfirmProvider>
    </div>
  )
}

export default App
